// Fuzzy plant name matching: handles typos, reversed word order, and partial matches.

const normalize = (s) => (s || '').toLowerCase().trim().replace(/\s+/g, ' ');

// Levenshtein distance for typo tolerance
const levenshtein = (a, b) => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  return matrix[b.length][a.length];
};

// Check if a query word fuzzy-matches any candidate word
const wordFuzzyMatch = (qw, cWords) => {
  const maxAllowed = Math.ceil(qw.length * 0.45);
  return cWords.some(cw => {
    if (cw === qw) return true;
    if (cw.startsWith(qw) || qw.startsWith(cw)) return true;
    if (qw.length >= 3) return levenshtein(qw, cw) <= maxAllowed;
    return false;
  });
};

// Score how well a candidate name matches the query.
// Higher score = better match. Returns 0 if no reasonable match.
const scoreMatch = (query, candidate) => {
  const q = normalize(query);
  const c = normalize(candidate);
  if (!q || !c) return 0;

  // Exact match
  if (c === q) return 1000;

  // Starts with query
  if (c.startsWith(q)) return 800;

  // Contains query as substring
  if (c.includes(q)) return 600;

  const qWords = q.split(' ').filter(Boolean);
  const cWords = c.split(' ').filter(Boolean);

  // All query words match exactly (any order) — e.g. "albo monstera" → "Monstera Albo"
  let exactWordMatches = 0;
  qWords.forEach(qw => {
    if (cWords.some(cw => cw === qw || cw.startsWith(qw) || qw.startsWith(cw))) exactWordMatches++;
  });
  if (exactWordMatches === qWords.length && qWords.length > 1) return 500;

  // All query words match with fuzzy typo tolerance (any order)
  // This handles "legacy moncera" → "Monstera Legacy" (reversed + typo)
  if (qWords.length > 1) {
    let fuzzyAllMatches = 0;
    qWords.forEach(qw => {
      if (qw.length < 2) { fuzzyAllMatches++; return; } // skip tiny words
      if (wordFuzzyMatch(qw, cWords)) fuzzyAllMatches++;
    });
    if (fuzzyAllMatches === qWords.length) return 450;
  }

  // Partial exact word matches
  if (exactWordMatches > 0) {
    const ratio = exactWordMatches / qWords.length;
    if (ratio >= 0.5) return Math.floor(300 * ratio);
  }

  // Partial fuzzy word matches
  let fuzzyPartialMatches = 0;
  qWords.forEach(qw => {
    if (qw.length < 3) return;
    if (wordFuzzyMatch(qw, cWords)) fuzzyPartialMatches++;
  });
  if (fuzzyPartialMatches > 0) {
    const ratio = fuzzyPartialMatches / qWords.length;
    if (ratio >= 0.5) return Math.floor(200 * ratio);
  }

  // Typo tolerance on whole string (for single-word queries)
  if (qWords.length === 1 && q.length >= 3) {
    const dist = levenshtein(q, c.slice(0, q.length + 3));
    const maxAllowed = Math.ceil(q.length * 0.4);
    if (dist <= maxAllowed) return 200 - dist * 10;
  }

  return 0;
};

/**
 * Search a list of plant records and return ranked matches.
 * Handles typos, reversed word order, and matches against
 * both common and scientific names.
 */
export const fuzzySearchPlants = (query, plants, limit = 8) => {
  if (!query || !query.trim()) return [];
  const results = [];
  const seen = new Set();

  plants.forEach((p) => {
    const name = (p.common_name || '').trim();
    if (!name || seen.has(name.toLowerCase())) return;

    const commonScore = scoreMatch(query, name);
    const sciScore = p.scientific_name ? scoreMatch(query, p.scientific_name) : 0;

    const bestScore = Math.max(commonScore, sciScore);
    if (bestScore > 0) {
      seen.add(name.toLowerCase());
      results.push({
        common_name: name,
        scientific_name: p.scientific_name || null,
        matchedOn: sciScore > commonScore ? 'scientific' : 'common',
        score: bestScore,
      });
    }
  });

  results.sort((a, b) => b.score - a.score);
  return results.slice(0, limit);
};

/**
 * Find the single best matching plant name for a query.
 */
export const findBestPlantMatch = (query, plants) => {
  const matches = fuzzySearchPlants(query, plants, 1);
  return matches.length > 0 ? matches[0] : null;
};